/**
 * The campaigns list: every open campaign, with its real progress.
 *
 * A card here is the smallest honest unit of the site: what the campaign is,
 * how far it has got, and - when nothing has come in yet - the invitation
 * instead of a zero. The numbers come from the API and the API computes them
 * from the ledger; this page holds no number and invents none.
 *
 * The contributor count on a card goes through the blind band
 * (`publicAggregates.ts`): a group smaller than the threshold is "fewer than
 * five", never a number small enough to name the donors inside it. A list is
 * the most scraped surface the site has, which is exactly why the band is
 * applied where the list is drawn.
 */
import type { PublicCampaign } from '@qalam/shared';
import { apiFetch } from '@qalam/shared/frontend';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  contributorCount,
  goalPercent,
  PRIVACY_MIN_GROUP_SIZE,
  raisedState,
} from '../publicAggregates';

export interface CampaignsListPageProps {
  /** Injected so a test can serve its own list without a network. */
  load?: () => Promise<PublicCampaign[]>;
}

function defaultLoad(): Promise<PublicCampaign[]> {
  return apiFetch<{ campaigns: PublicCampaign[] }>('/api/public/campaigns')
    .then((payload) => payload.campaigns)
    .catch(() => []);
}

export default function CampaignsListPage({ load = defaultLoad }: CampaignsListPageProps) {
  const { t, i18n } = useTranslation();
  const [campaigns, setCampaigns] = useState<PublicCampaign[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void load()
      .then((found) => {
        if (!cancelled) setCampaigns(found);
      })
      .catch(() => {
        if (!cancelled) setCampaigns([]);
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  if (campaigns === null) {
    return (
      <main data-testid="campaigns-list">
        <h1 data-testid="campaigns-title">{t('publicAggregates.listTitle')}</h1>
        <p data-testid="campaigns-loading">{t('publicAggregates.loading')}</p>
      </main>
    );
  }

  if (campaigns.length === 0) {
    return (
      <main data-testid="campaigns-list">
        <h1 data-testid="campaigns-title">{t('publicAggregates.listTitle')}</h1>
        <p data-testid="campaigns-none">{t('publicAggregates.none')}</p>
      </main>
    );
  }

  const arabic = i18n.language?.startsWith('ar');

  return (
    <main data-testid="campaigns-list">
      <h1 data-testid="campaigns-title">{t('publicAggregates.listTitle')}</h1>
      {campaigns.map((campaign) => {
        const raised = raisedState(campaign.raisedCents);
        // The bar measures the goal's own unit: seats against a seat goal,
        // money against a money goal. Nothing yet fills nothing, because the
        // aggregate behind it is nothing.
        const done =
          campaign.goalUnit === 'SEATS' ? campaign.seatsFunded : (campaign.raisedCents ?? 0);
        const percent = goalPercent(done, campaign.goalAmount);
        const contributors = contributorCount(campaign.contributors);
        return (
          <article key={campaign.slug} data-testid={`campaign-card-${campaign.slug}`}>
            <h2>
              <Link
                to={`/campaigns/${campaign.slug}`}
                data-testid={`campaign-link-${campaign.slug}`}
              >
                {arabic ? campaign.titleAr : campaign.titleEn}
              </Link>
            </h2>
            {raised.nothingYet ? (
              <p data-testid={`campaign-empty-${campaign.slug}`}>
                {t('publicAggregates.beTheFirst')}
              </p>
            ) : (
              <p data-testid={`campaign-raised-${campaign.slug}`}>
                {t('publicAggregates.raised', { amount: (raised.amountCents / 100).toFixed(2) })}
              </p>
            )}
            <div
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              data-testid={`campaign-bar-${campaign.slug}`}
              style={{
                background: 'rgba(0, 0, 0, 0.12)',
                blockSize: '0.5rem',
                inlineSize: '100%',
              }}
            >
              <div
                style={{
                  background: '#e7b75f',
                  blockSize: '0.5rem',
                  inlineSize: `${percent}%`,
                }}
              />
            </div>
            <p data-testid={`campaign-seats-${campaign.slug}`}>
              {t('publicAggregates.seats', { count: campaign.seatsFunded })}
            </p>
            <p data-testid={`campaign-contributors-${campaign.slug}`}>
              {contributors.smallGroup
                ? t('publicAggregates.contributorsFewerThan', { count: PRIVACY_MIN_GROUP_SIZE })
                : t('publicAggregates.contributors', { count: contributors.count })}
            </p>
            {campaign.deadline ? (
              <p data-testid={`campaign-deadline-${campaign.slug}`}>
                {t('campaign.deadline', { date: campaign.deadline.slice(0, 10) })}
              </p>
            ) : null}
          </article>
        );
      })}
    </main>
  );
}
